import { forwardRef } from 'react';
import { ChevronDown, Store } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEnrolledTenants } from '@/hooks/use-enrolled-tenants';
import { useTenant } from '@/hooks/use-tenant';
import { cn } from '@/lib/utils';

export interface StoreSwitcherTriggerProps {
  onOpen: () => void;
  className?: string;
}

/**
 * Active store in the mobile header (SHELL-04 AC 1/5): a button with a
 * chevron with more than one store; plain text with one, or while the list
 * is loading or failed (the current store is always shown).
 */
export const StoreSwitcherTrigger = forwardRef<
  HTMLButtonElement,
  StoreSwitcherTriggerProps
>(({ onOpen, className }, ref) => {
  const tenant = useTenant();
  const { data: tenants } = useEnrolledTenants();
  const canSwitch = (tenants?.length ?? 0) > 1;

  const identity = (
    <>
      <Avatar className="size-7 shrink-0 rounded-lg">
        <AvatarImage src={tenant.iconUrl} alt="" />
        <AvatarFallback className="rounded-lg">
          <Store className="size-3.5" />
        </AvatarFallback>
      </Avatar>
      <span className="truncate font-semibold">{tenant.name}</span>
    </>
  );

  if (!canSwitch) {
    return (
      <div
        className={cn('flex min-h-touch min-w-0 items-center gap-2', className)}
      >
        {identity}
      </div>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      aria-haspopup="dialog"
      onClick={onOpen}
      className={cn(
        'flex min-h-touch min-w-0 items-center gap-2 rounded-lg pr-1 outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
    >
      {identity}
      <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );
});
StoreSwitcherTrigger.displayName = 'StoreSwitcherTrigger';
